import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { database } from '../config/firebase';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';

interface Response {
  value: number;
  timestamp: any;
}

interface SessionMetadata {
  isActive: boolean;
  createdAt: any;
  participantCount: number;
  lastActivity: any;
}

interface Question {
  id: string;
  text: string;
  createdAt: number;
  createdBy: string;
}

interface FirebaseContextType {
  connectionStatus: 'connected' | 'connecting' | 'disconnected';
  participantCount: number;
  responses: Record<string, Response>;
  sessionInfo: {
    participantCount: number;
    responseCount: number;
    average: number;
    isActive: boolean;
  } | null;
  currentQuestion: string | null;
  questions: Question[];
  userId: string | null;
  // Methods
  joinAsParticipant: () => void;
  updateResponse: (value: number) => void;
  resetSession: () => void;
  clearResponses: () => void;
  setCurrentQuestion: (question: string) => void;
  addQuestion: (text: string) => Promise<void>;
  deleteQuestion: (questionId: string) => Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextType>({
  connectionStatus: 'disconnected',
  participantCount: 0,
  responses: {},
  sessionInfo: null,
  currentQuestion: null,
  questions: [],
  userId: null,
  joinAsParticipant: () => {},
  updateResponse: () => {},
  resetSession: () => {},
  clearResponses: () => {},
  setCurrentQuestion: () => {},
  addQuestion: async () => {},
  deleteQuestion: async () => {}
});

interface FirebaseProviderProps {
  children: ReactNode;
}

export const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [participantCount, setParticipantCount] = useState<number>(0);
  const [responses, setResponses] = useState<Record<string, Response>>({});
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [currentQuestion, setCurrentQuestionState] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // Generate unique user ID on mount
  useEffect(() => {
    const id = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setUserId(id);
  }, []);

  // Set up Firebase connection status monitoring
  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
      if (snapshot.val() === true) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen to session responses
  useEffect(() => {
    const responsesRef = ref(database, 'sessions/current/responses');
    const unsubscribe = onValue(responsesRef, (snapshot) => {
      const data = snapshot.val();
      setResponses(data || {});
    });

    return () => unsubscribe();
  }, []);

  // Listen to current question
  useEffect(() => {
    const questionRef = ref(database, 'sessions/current/question');
    const unsubscribe = onValue(questionRef, (snapshot) => {
      const question = snapshot.val();
      setCurrentQuestionState(question || null);
    });

    return () => unsubscribe();
  }, []);

  // Listen to questions library
  useEffect(() => {
    const questionsRef = ref(database, 'questions');
    const unsubscribe = onValue(questionsRef, (snapshot) => {
      const questionsData = snapshot.val();
      if (questionsData) {
        // Convert object to array and sort by creation date (newest first)
        const questionsArray = Object.values(questionsData) as Question[];
        questionsArray.sort((a, b) => b.createdAt - a.createdAt);
        setQuestions(questionsArray);
      } else {
        setQuestions([]);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen to session metadata
  useEffect(() => {
    const metadataRef = ref(database, 'sessions/current/metadata');
    const unsubscribe = onValue(metadataRef, (snapshot) => {
      const metadata: SessionMetadata = snapshot.val();
      if (metadata) {
        setParticipantCount(metadata.participantCount || 0);
        
        // Calculate session info
        const responseValues = Object.values(responses);
        const average = responseValues.length > 0 
          ? Math.round(responseValues.reduce((sum, r) => sum + r.value, 0) / responseValues.length)
          : 0;
        
        setSessionInfo({
          participantCount: metadata.participantCount || 0,
          responseCount: responseValues.length,
          average,
          isActive: metadata.isActive || false
        });
      }
    });

    return () => unsubscribe();
  }, [responses]);

  // Set up disconnect handling for user cleanup
  useEffect(() => {
    if (userId && connectionStatus === 'connected') {
      const userRef = ref(database, `sessions/current/participants/${userId}`);
      const userResponseRef = ref(database, `sessions/current/responses/${userId}`);
      
      // Set user as online
      set(userRef, { joinedAt: serverTimestamp(), isOnline: true });
      
      // Set up disconnect cleanup
      onDisconnect(userRef).remove();
      onDisconnect(userResponseRef).remove();
      
      // Update participant count when user connects
      onValue(ref(database, 'sessions/current/participants'), (snapshot) => {
        const participants = snapshot.val();
        const count = participants ? Object.keys(participants).length : 0;
        set(ref(database, 'sessions/current/metadata/participantCount'), count);
      });
    }
  }, [userId, connectionStatus]);

  const joinAsParticipant = () => {
    if (userId && connectionStatus === 'connected') {
      const userRef = ref(database, `sessions/current/participants/${userId}`);
      set(userRef, { 
        joinedAt: serverTimestamp(), 
        isOnline: true 
      });
    }
  };

  const updateResponse = (value: number) => {
    if (userId && connectionStatus === 'connected') {
      const responseRef = ref(database, `sessions/current/responses/${userId}`);
      set(responseRef, {
        value: Math.max(0, Math.min(100, value)), // Clamp between 0-100
        timestamp: serverTimestamp()
      });
      
      // Update last activity
      const lastActivityRef = ref(database, 'sessions/current/metadata/lastActivity');
      set(lastActivityRef, serverTimestamp());
    }
  };

  const resetSession = () => {
    if (connectionStatus === 'connected') {
      // Clear all responses
      const responsesRef = ref(database, 'sessions/current/responses');
      set(responsesRef, null);
      
      // Clear current question
      const questionRef = ref(database, 'sessions/current/question');
      set(questionRef, null);
      
      // Reset metadata
      const metadataRef = ref(database, 'sessions/current/metadata');
      set(metadataRef, {
        isActive: true,
        createdAt: serverTimestamp(),
        participantCount: 0,
        lastActivity: serverTimestamp()
      });
      
      // Clear participants (they'll rejoin automatically)
      const participantsRef = ref(database, 'sessions/current/participants');
      set(participantsRef, null);
    }
  };

  const clearResponses = () => {
    if (connectionStatus === 'connected') {
      // Clear all responses
      const responsesRef = ref(database, 'sessions/current/responses');
      set(responsesRef, null);
      
      // Reset metadata (but keep question)
      const metadataRef = ref(database, 'sessions/current/metadata');
      set(metadataRef, {
        isActive: true,
        createdAt: serverTimestamp(),
        participantCount: 0,
        lastActivity: serverTimestamp()
      });
      
      // Clear participants (they'll rejoin automatically)
      const participantsRef = ref(database, 'sessions/current/participants');
      set(participantsRef, null);
    }
  };

  const setCurrentQuestion = (question: string) => {
    if (connectionStatus === 'connected') {
      const questionRef = ref(database, 'sessions/current/question');
      set(questionRef, question);
    }
  };

  const addQuestion = async (text: string): Promise<void> => {
    if (connectionStatus === 'connected' && userId) {
      const newQuestion: Question = {
        id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: text.trim(),
        createdAt: Date.now(),
        createdBy: userId
      };
      
      const questionRef = ref(database, `questions/${newQuestion.id}`);
      await set(questionRef, newQuestion);
    }
  };

  const deleteQuestion = async (questionId: string): Promise<void> => {
    if (connectionStatus === 'connected') {
      const questionRef = ref(database, `questions/${questionId}`);
      await set(questionRef, null);
    }
  };

  const value: FirebaseContextType = {
    connectionStatus,
    participantCount,
    responses,
    sessionInfo,
    currentQuestion,
    questions,
    userId,
    joinAsParticipant,
    updateResponse,
    resetSession,
    clearResponses,
    setCurrentQuestion,
    addQuestion,
    deleteQuestion
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextType => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
}; 